import { LocalDatabaseService } from './local-database.service';
import { Injectable } from '@angular/core';
import { Note, TodoItemNode } from '../../model/notes.model';
import { Firestore, onSnapshot, collection, WriteBatch, doc, addDoc, updateDoc, writeBatch, getFirestore } from 'firebase/firestore';
import { AuthService } from '../authentication/auth.service';
import { ref, uploadBytesResumable, deleteObject, getStorage } from 'firebase/storage';

@Injectable({
  providedIn: 'root'
})
export class NotesFirestoreService {

  private localDb: LocalDatabaseService;

  private firestore: Firestore = getFirestore();
  private storage = getStorage();

  constructor(
    private authService: AuthService) { }


  /**
   * Sets the local database service for the NotesFirestoreService.
   * @param localDb The ChecklistDatabaseService instance to set as the local database.
   */
  setLocalDb(localDb: LocalDatabaseService) {
    this.localDb = localDb;
  }


  /**
   * Starts listening for changes in the notes collection of a specific notebook.
   * @param notebookItem The notebook item to listen for.
   */
  startNotesListenerForNotebook(notebookItem: TodoItemNode) {
    // se primeiro snaphot - não chama noteAddAction, faz como antes
    // primeiro monta o map para os Notes

    //console.log("Before start listener for notebook = " + notebookItem.item);

    notebookItem.notesListeningStarted = true;

    const userId = this.authService.userId;
    const notesRef = collection(this.firestore, `users/${userId}/notebooks/${notebookItem.id}/notes`);

    onSnapshot(notesRef, (snapshot) => {

      console.log("listen for notebook = " + notebookItem.id);

      // If first time is loading - load all
      if (!(notebookItem.subscribeChildren || notebookItem.childrenNotesLoaded)) {


        notebookItem.childrenNotesLoaded = true;

        //console.log("pegando todos de uma vez");

        // mapeia para notes
        let loadeddNotes: Note[] = snapshot.docChanges().map(a => {
          const id = a.doc.id;
          const data = a.doc.data() as Note;
          return { id, ...data };
        })

        this.localDb.gatherAllNotesFromNotebook(notebookItem, loadeddNotes);

        notebookItem.childrenNotesLoaded = true;

        return;
      }

      snapshot.docChanges().forEach((change) => {

        const data = change.doc.data();
        const id = change.doc.id;

        if (change.type === "added") {
          //console.log("New Note: ", change.doc.data());
          this.localDb.noteAddAction(notebookItem, { id, ...data } as Note);
        }
        if (change.type === "modified") {
          //console.log("Modified Note: ", change.doc.data());
          this.localDb.noteModifiedAction(notebookItem, { id, ...data } as Note);
        }
        if (change.type === "removed") {
          //console.log("Removed Note: ", change.doc.data());
          this.localDb.noteRemovedAction(notebookItem, { id, ...data } as Note);
        }
      });
    });
  }

  async addNewNote(parent: TodoItemNode, text: string, imageFile?: File) {
    const uid = this.authService.userId; // Certifique-se de que este serviço forneça o UID do usuário autenticado

    //falta só pegar prioridade nova correta - não sabe qual é a do pai.. mas só no caso quando for notebook nao vai saber
    // se for note vai saber pq vai ter carregado. ver se da pra ler o tamanho da colecao
    // ou quando adicionado o max number - atualiza

    if (imageFile) {
      const filePath = `users/${uid}/images/notebooks/${parent.notebook_id}/${imageFile.name}`;
      const fileRef = ref(this.storage, filePath);

      //this.addNoteDocument(parent, text, filePath); // Função separada para adicionar o documento

      // Upload da imagem
      const uploadTask = await uploadBytesResumable(fileRef, imageFile);

      // Após o upload, adicione o documento no Firestore com o caminho da imagem
      return this.addNoteDocument(parent, text, filePath); // Função separada para adicionar o documento
    } else {
      // Se não houver arquivo de imagem, adicione o documento sem o caminho da imagem
      return this.addNoteDocument(parent, text);
    }
  }

  // Função separada para adicionar o documento no Firestore para reutilização
  private async addNoteDocument(parent: TodoItemNode, text: string, imagePath?: string) {
    parent.maxChildrenPriorityNumber += 100;
    const priority = parent.maxChildrenPriorityNumber;

    const noteData: Note = {
      text: text,
      parent_id: parent.id,
      has_child: false,
      priority: priority,
      notebook_id: parent.notebook_id,
      max_children_priority: 0,
      createdAt: new Date(),
    };

    if (imagePath) {
      noteData.image_path = imagePath; // Adiciona o caminho da imagem se fornecido
    }

    const notesCollectionRef = collection(
      this.firestore,
      `/users/${this.authService.userId}/notebooks/${parent.notebook_id}/notes`
    );
    const noteDocument = await addDoc(notesCollectionRef, noteData);
    noteData.id = noteDocument.id;

    if (!parent.has_child && !parent.parentRef && !parent.subscribeChildren) {
      parent.has_child = true;
      this.localDb.dataChange.next(this.localDb.data);
    }

    // Update notebook if the parent is the notebook itself
    // aqui faz - se adiciona direto no notebook como pai - atualiza notebook max priority
    // TODO -- nos listerns do notebook, se nao tem nota dentro ao testar - atualiza no banco o notebook para has_child=false
    //  testa para ver se tem bug
    if (parent.id === parent.notebook_id) {
      const notebookDocRef = doc(
        this.firestore,
        `users/${this.authService.userId}/notebooks/${parent.id}`
      );
      await updateDoc(notebookDocRef, {
        has_child: true,
        max_children_priority: priority,
        lastChangeTime: new Date(),
      });
    } else {
      // autualiza o pai se nao for notebook também
      const parentNoteDocRef = doc(
        this.firestore,
        `users/${this.authService.userId}/notebooks/${parent.notebook_id}/notes/${parent.id}`
      );
      await updateDoc(parentNoteDocRef, {
        has_child: true,
        max_children_priority: priority,
        lastChangeTime: new Date(),
      });

      // update last change time too for notebook
      const notebookDocRef = doc(
        this.firestore,
        `users/${this.authService.userId}/notebooks/${parent.notebook_id}`
      );
      await updateDoc(notebookDocRef, {
        lastChangeTime: new Date(),
      });
    }

    // Update current location in the database if necessary
    // se parent = insertion point -- atualiza tmb o current location no DB para a prioridade certa
    const ipLocation = this.localDb.getInsertPointLocation();
    if (ipLocation && parent.id === ipLocation.parent_id) {
      const insertionPointDocRef = doc(
        this.firestore,
        `/users/${this.authService.userId}/settings/insertion_point`
      );
      await updateDoc(insertionPointDocRef, {
        priority: parent.maxChildrenPriorityNumber,
      });
    }

    return noteData;
  }


  /**
   * Recursively deletes a node and its children from the remote Firestore database.
   * @param batch The Firestore batch object to perform the deletion.
   * @param n The node to delete.
   */
  deleteMeAndMyChildrenFromRemoteDB(batch: ReturnType<typeof writeBatch>, n: TodoItemNode) {
    // Check if the node has children
    if (n.children && n.children.length) {
      // Recursively delete each child node
      n.children.forEach((node) => {
        this.deleteMeAndMyChildrenFromRemoteDB(batch, node);
      });
    }

    // Reference to the document in Firestore
    const docRef = doc(
      this.firestore,
      `users/${this.authService.userId}/notebooks/${n.notebook_id}/notes/${n.id}`
    );

    // Delete the node from the Firestore database
    batch.delete(docRef);
  }

  /**
   * Deletes a Note from the Firestore database.
   *
   * @param node - The TodoItemNode to be deleted.
   * @returns A Promise that resolves to the parent reference of the deleted node.
   */
  deleteItem(node: TodoItemNode) {
    // apaga todo os filhos primeiro
    // se tira do nodo pai.. se era ultimo ve se tem q fazer alguma coisa
    // se destroy

    // atualiza o data change

    // Associated images are deleted by the deleteImageOnNoteDelete Cloud Function.

    const notebookId = node.notebook_id;

    // checa se root
    if (!node.parentRef) {
      //return this.deleteNotebook(node);
      return new Promise(null);
      // nao vai chegar aqui pq  a logica no view nao vai deixar
    }

    const batch = writeBatch(this.firestore);

    this.deleteMeAndMyChildrenFromRemoteDB(batch, node);

    return batch.commit().then(async result => {

      /*
        this.deleteMyChildrenFromLocalDB(node);

        //delete me now
        const parent_ref = node.parentRef;
        let childrenArray = node.parentRef.children;
        if (childrenArray && (childrenArray.length > 0))
           childrenArray.splice(childrenArray.findIndex(itemNode => {return itemNode.id == node.id}), 1);
         this.dataChange.next(this.data);

        */
         const notebookRef = doc(
          this.firestore,
          `users/${this.authService.userId}/notebooks/${notebookId}`
        );

        // Atualiza o notebook
        await updateDoc(notebookRef, {
          lastChangeTime: new Date(),
        });

      const parent_ref = node.parentRef;
      return parent_ref;
    }).catch(error => {
      console.log("Opsss - error when deleting: " + error);
    });

    // se deleta no banco -- se ok - executa bloco abaixo
    // caso especial se for o caderno

  }

  /**
   * Updates a note in the Firestore database.
   *
   * @param note - The Note object to update. (Alwaey specify the id of the note to update and notebook_id.)
   */
  async updateNote(note: Partial<Note>) {
    // Referência ao notebook no Firestore
    const notebookRef = doc(
      this.firestore,
      `users/${this.authService.userId}/notebooks/${note.notebook_id}`
    );

    // Atualiza o notebook
    const notebookUpdatePromise = updateDoc(notebookRef, {
      lastChangeTime: new Date(),
    });

    // Referência à nota no Firestore
    const noteRef = doc(
      this.firestore,
      `users/${this.authService.userId}/notebooks/${note.notebook_id}/notes/${note.id}`
    );

    // Salva a nota no Firestore
    const noteUpdatePromise = updateDoc(noteRef, note);

    // Aguarda a conclusão de ambas as promessas
    return Promise.all([notebookUpdatePromise, noteUpdatePromise]);
  }


  async uploadImageAndUpdateNote(note: Note, imageFile: File) {
    const filePath = `users/${this.authService.userId}/images/notebooks/${note.notebook_id}/${imageFile.name}`;
    const fileRef = ref(this.storage, filePath);

    note.image_path = filePath;


    // Upload da imagem
    const uploadTask = await uploadBytesResumable(fileRef, imageFile);


    await  this.updateNote(note);

  }

  deleteImageFromStorage(imagePath: string) {
    const imageRef = ref(this.storage, imagePath);
    return deleteObject(imageRef);
  }

}

